import React, { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';

/**
 * Auto-dismissing toast notification.
 *
 * Props:
 *   message       — primary text (e.g. "Tomato added to My Plants")
 *   onEditDetails — () => void — optional; shows "Edit details →" link when provided
 *   onDismiss     — () => void — called after auto-dismiss (3 s) or manual close
 */
const Toast = ({ message, onEditDetails, onDismiss }) => {
    // Keep a ref to the latest onDismiss so the timer isn't restarted
    // if the caller passes a new inline function on each render.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => { onDismissRef.current = onDismiss; });

    useEffect(() => {
        const timer = setTimeout(() => {
            onDismissRef.current?.();
        }, 3000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    return (
        <div
            role="status"
            aria-live="polite"
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-3 px-4 py-3 bg-[#2F5233] text-white rounded-2xl shadow-xl text-sm w-[calc(100vw-2rem)] max-w-sm"
            style={{ minWidth: 260 }}
        >
            {/* Success icon */}
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#52A675] flex items-center justify-center">
                <Check size={14} strokeWidth={2.5} />
            </span>

            {/* Message */}
            <span className="flex-1 font-medium">{message}</span>

            {/* Edit details link — only shown when caller provides the handler */}
            {onEditDetails && (
                <button
                    onClick={onEditDetails}
                    className="flex-shrink-0 text-[#C7F464] hover:text-white font-semibold underline underline-offset-2 transition-colors whitespace-nowrap"
                >
                    Edit details &rarr;
                </button>
            )}

            {/* Close button */}
            <button
                onClick={onDismiss}
                className="flex-shrink-0 text-white/60 hover:text-white transition-colors"
                aria-label="Dismiss"
            >
                <X size={14} />
            </button>
        </div>
    );
};

export default Toast;
